/* THE SEARCH PALETTE.
 *
 * The console can act on 1,183 spells and cannot show one; "what does Faerie
 * Fire actually say?" means another tab, which is the context switch this
 * product exists to remove. The review called this the single highest-value
 * feature in it, and said why it is cheap: the data is already in memory. This
 * is an index and a reader, not an acquisition problem.
 *
 * WHAT IS SEARCHED is declared by the caller as a list of sources, so adding the
 * next kind of thing is a line there and nothing here. What this module owns is
 * the ranking, and ranking is the whole difference between a palette a DM reaches
 * for mid-sentence and one they stop using on the second try.
 *
 * THE RANKING RULES, in the order they matter:
 *
 *   1. An exact name wins outright. Typing "haste" and getting "Hastened Step"
 *      first is the failure that makes a palette untrustworthy.
 *   2. Then a name that STARTS with the query, then a name with a WORD starting
 *      with it ("fire" finds "Faerie Fire"), then a name containing it.
 *   3. Body text ranks below every name match, always. A spell whose text
 *      mentions "haste" must never outrank the spell called Haste.
 *   4. Ties break by kind weight -- the things at the table beat the things in a
 *      book -- and then by name, so the order never jitters between keystrokes.
 *
 * Deliberately NOT fuzzy. Subsequence matching ("frbl" finds "Fireball") looks
 * clever and ranks nonsense highly on short queries, which is most queries typed
 * one letter at a time. Initials are handled instead, because "AoE" and "DC" are
 * how people actually abbreviate.
 */
export interface SearchItem {
  /** stable, unique: the kind and the thing's own id */
  id: string;
  kind: string;
  /** what the DM typed to look for */
  name: string;
  /** a line under the name: level and school, CR and type, a condition summary */
  detail?: string;
  /** everything else worth matching but not worth showing in the row */
  body?: string;
  /** what pressing Enter does, named for the row's own button */
  action?: { verb: string; nav?: string; statBlock?: string; payload?: unknown };
}

export interface Hit extends SearchItem { score: number; why: Why }
export type Why = "exact" | "prefix" | "word" | "name" | "detail" | "body";

/** How much each kind is worth when two things match equally well. The things in
 *  front of the DM beat the things in a book: a creature on the tracker is more
 *  likely to be what "goblin" meant than a goblin in the catalogue. */
export const KIND_WEIGHT: Record<string, number> = {
  tracker: 60, party: 55, page: 50, condition: 45, ruling: 40, action: 40, mastery: 40,
  spell: 35, monster: 25, library: 30, table: 20, item: 20,
};

const norm = (v: string): string => String(v || "").toLowerCase().replace(/[’']/g, "").replace(/\s+/g, " ").trim();

/** The first letter of each word: "Faerie Fire" gives "ff". Used so an
 *  abbreviation the DM already says out loud finds the thing. */
export const initials = (name: string): string =>
  norm(name).split(/[^a-z0-9]+/).filter(Boolean).map((w) => w[0]).join("");

const WHY_SCORE: Record<Why, number> = { exact: 10000, prefix: 5000, word: 3000, name: 1500, detail: 600, body: 200 };

/** Why this item matched, or null for not at all. */
export function matchKind(item: SearchItem, q: string): Why | null {
  const name = norm(item.name);
  if (!q) return null;
  if (name === q) return "exact";
  /* An abbreviation counts as an exact hit only when it is the WHOLE query and
     more than one letter -- otherwise every single letter would "exactly" match
     a hundred things. */
  if (q.length > 1 && initials(item.name) === q) return "exact";
  if (name.startsWith(q)) return "prefix";
  if (name.split(/[^a-z0-9]+/).some((w) => w.startsWith(q))) return "word";
  if (name.includes(q)) return "name";
  if (norm(item.detail || "").includes(q)) return "detail";
  if (norm(item.body || "").includes(q)) return "body";
  return null;
}

/** Rank the index against a query. An empty query returns nothing: a palette
 *  that opens full of arbitrary rows teaches the DM to ignore the list. */
export function rank(items: SearchItem[], query: string, limit = 40): Hit[] {
  const q = norm(query);
  if (!q) return [];
  const hits: Hit[] = [];
  for (const item of items) {
    const why = matchKind(item, q);
    if (!why) continue;
    /* A shorter name that matched the same way is the better hit: "Haste" over
       "Hastened Step" for "hast". Capped so a long name is penalised but never
       outranked by a worse kind of match. */
    const brevity = Math.max(0, 40 - norm(item.name).length);
    hits.push({ ...item, why, score: WHY_SCORE[why] + (KIND_WEIGHT[item.kind] ?? 10) + brevity });
  }
  hits.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  return hits.slice(0, limit);
}

/** Results grouped by kind for display, keeping each group in ranked order and
 *  the groups themselves in the order their best hit appeared. */
export function group(hits: Hit[]): { kind: string; hits: Hit[] }[] {
  const out: { kind: string; hits: Hit[] }[] = [];
  const index = new Map<string, { kind: string; hits: Hit[] }>();
  for (const h of hits) {
    const had = index.get(h.kind);
    if (had) { had.hits.push(h); continue; }
    const g = { kind: h.kind, hits: [h] };
    index.set(h.kind, g); out.push(g);
  }
  return out;
}

export const KIND_LABEL: Record<string, string> = {
  tracker: "On the tracker", party: "The party", page: "Go to", condition: "Conditions",
  ruling: "Rulings", action: "Actions in combat", mastery: "Weapon masteries", spell: "Spells", monster: "Monsters",
  library: "Your library", table: "Random tables", item: "Items",
};
export const labelFor = (kind: string): string => KIND_LABEL[kind] || kind;

/** Where in the name the query landed, so the row can show it. Returns null when
 *  the match was in the detail or the body, which is why the row says so
 *  instead: a hit the DM cannot see the reason for reads as a wrong answer. */
export function highlight(name: string, query: string): [string, string, string] | null {
  /* Matched with a LENGTH-PRESERVING fold, not with norm().
     norm strips apostrophes and collapses whitespace, so an index found in the
     normalised string does not point at the same character in the original -- a
     search in "Bigby's Hand" would have sliced one character off. Case folding
     keeps every index where it was, so the slice is always right. The cost is
     that a match only norm() can see (across an apostrophe, say) simply does not
     highlight, and the row shows why it matched instead. */
  const q = String(query || "").trim().toLowerCase();
  if (!q) return null;
  const at = String(name || "").toLowerCase().indexOf(q);
  if (at < 0) return null;
  return [name.slice(0, at), name.slice(at, at + q.length), name.slice(at + q.length)];
}
